import React from 'react';
import { Box, Stack } from '@mui/material';
import type { ExecuteCheckpointDef, ExecuteCheckpointFieldType } from '@/types/run-stage.types';
import { useExecuteStepStore } from '@/run-stage/store/executeStepStore';
import { CHECKPOINT_COMPONENT_MAP } from './checkpoints';
import { CheckpointGroup } from './checkpoints/CheckpointGroup';

interface CheckpointListProps {
  checkpoints: ExecuteCheckpointDef[];
  isReadOnly?: boolean;
}

export const CheckpointList: React.FC<CheckpointListProps> = ({ checkpoints, isReadOnly = false }) => {
  const setCheckpointValue = useExecuteStepStore((s) => s.setCheckpointValue);
  const dirtyCheckpoints = useExecuteStepStore((s) => s.dirtyCheckpoints);
  const checkpointErrors = useExecuteStepStore((s) => s.checkpointErrors);

  if (!checkpoints.length) return null;

  return (
    <Stack spacing={1.5}>
      {checkpoints.map((cp) => {
        const id = cp.id || cp._id || '';
        const fieldType = cp.fieldType as ExecuteCheckpointFieldType | undefined;

        if (!fieldType) return null;

        // Group type has its own recursive render
        if (fieldType === 'group') {
          return (
            <Box key={id}>
              <CheckpointGroup checkpoint={cp} isReadOnly={isReadOnly} />
            </Box>
          );
        }

        const Component = CHECKPOINT_COMPONENT_MAP[fieldType];
        if (!Component) return null;

        const dirtyValue = dirtyCheckpoints.get(id)?.value;
        const currentValue = dirtyValue !== undefined ? dirtyValue : undefined;
        const error = checkpointErrors.get(id);

        return (
          <Box key={id}>
            <Component
              checkpoint={cp}
              value={currentValue}
              onChange={(val: unknown) => setCheckpointValue(id, val)}
              error={error}
              isReadOnly={isReadOnly}
            />
          </Box>
        );
      })}
    </Stack>
  );
};
